import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import "./SuperAdminDashboard.css";

const SuperAdminDashboard = ({ user }) => {
  const [activeTab, setActiveTab] = useState("users");
  const [users, setUsers] = useState([]);
  const [dailyItems, setDailyItems] = useState([]);
  const [deliveryLogs, setDeliveryLogs] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState("create");

  // Form states
  const [userForm, setUserForm] = useState({
    username: "",
    email: "",
    password: "",
    role: "user",
    firstName: "",
    lastName: "",
  });

  const [dailyItemForm, setDailyItemForm] = useState({
    title: "",
    description: "",
    assignedTo: "",
    createdBy: "",
    startDate: "",
    endDate: "",
    priority: "medium",
    status: "pending",
  });

  const [deliveryLogForm, setDeliveryLogForm] = useState({
    dailyItem: "",
    assignedTo: "",
    deliveryDate: "",
    status: "pending",
    deliveredBy: "",
    notes: "",
  });

  const [eventForm, setEventForm] = useState({
    title: "",
    description: "",
    start: "",
    end: "",
    allDay: false,
    color: "#3174ad",
  });

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const config = {
        headers: { Authorization: `Bearer ${token}` },
      };

      switch (activeTab) {
        case "users":
          const usersResponse = await axios.get(
            "https://dailybackend-zvvt.onrender.com/api/users/superadmin/all-users",
            config
          );
          setUsers(usersResponse.data);
          break;
        case "dailyItems":
          const itemsResponse = await axios.get(
            "https://dailybackend-zvvt.onrender.com/api/users/superadmin/daily-items",
            config
          );
          setDailyItems(itemsResponse.data);
          break;
        case "deliveryLogs":
          const deliveryResponse = await axios.get(
            "https://dailybackend-zvvt.onrender.com/api/users/superadmin/delivery-logs",
            config
          );
          setDeliveryLogs(deliveryResponse.data);
          break;
        case "events":
          const eventsResponse = await axios.get(
            "https://dailybackend-zvvt.onrender.com/api/users/superadmin/events",
            config
          );
          setEvents(eventsResponse.data);
          break;
        default:
          break;
      }
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem("token");
      const config = {
        headers: { Authorization: `Bearer ${token}` },
      };

      let endpoint = "";
      let data = {};

      switch (activeTab) {
        case "users":
          endpoint =
            "https://dailybackend-zvvt.onrender.com/api/users/superadmin/users";
          data = userForm;
          break;
        case "dailyItems":
          endpoint =
            "https://dailybackend-zvvt.onrender.com/api/users/superadmin/daily-items";
          data = dailyItemForm;
          break;
        case "deliveryLogs":
          endpoint =
            "https://dailybackend-zvvt.onrender.com/api/users/superadmin/delivery-logs";
          data = deliveryLogForm;
          break;
        case "events":
          endpoint =
            "https://dailybackend-zvvt.onrender.com/api/users/superadmin/events";
          data = eventForm;
          break;
        default:
          return;
      }

      await axios.post(endpoint, data, config);
      toast.success(`${activeTab.slice(0, -1)} created successfully!`);
      setShowModal(false);
      resetForm();
      loadData();
    } catch (error) {
      console.error("Error creating item:", error);
      toast.error("Failed to create item");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem("token");
      const config = {
        headers: { Authorization: `Bearer ${token}` },
      };

      let endpoint = "";
      let data = {};

      switch (activeTab) {
        case "users":
          endpoint = `https://dailybackend-zvvt.onrender.com/api/users/superadmin/users/${editingItem._id}`;
          data = userForm;
          break;
        case "dailyItems":
          endpoint = `https://dailybackend-zvvt.onrender.com/api/users/superadmin/daily-items/${editingItem._id}`;
          data = dailyItemForm;
          break;
        case "deliveryLogs":
          endpoint = `https://dailybackend-zvvt.onrender.com/api/users/superadmin/delivery-logs/${editingItem._id}`;
          data = deliveryLogForm;
          break;
        case "events":
          endpoint = `https://dailybackend-zvvt.onrender.com/api/users/superadmin/events/${editingItem._id}`;
          data = eventForm;
          break;
        default:
          return;
      }

      await axios.put(endpoint, data, config);
      toast.success(`${activeTab.slice(0, -1)} updated successfully!`);
      setShowModal(false);
      setEditingItem(null);
      resetForm();
      loadData();
    } catch (error) {
      console.error("Error updating item:", error);
      toast.error("Failed to update item");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this item?")) {
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const config = {
        headers: { Authorization: `Bearer ${token}` },
      };

      let endpoint = "";
      switch (activeTab) {
        case "users":
          endpoint = `https://dailybackend-zvvt.onrender.com/api/users/superadmin/users/${id}`;
          break;
        case "dailyItems":
          endpoint = `https://dailybackend-zvvt.onrender.com/api/users/superadmin/daily-items/${id}`;
          break;
        case "deliveryLogs":
          endpoint = `https://dailybackend-zvvt.onrender.com/api/users/superadmin/delivery-logs/${id}`;
          break;
        case "events":
          endpoint = `https://dailybackend-zvvt.onrender.com/api/users/superadmin/events/${id}`;
          break;
        default:
          return;
      }

      await axios.delete(endpoint, config);
      toast.success(`${activeTab.slice(0, -1)} deleted successfully!`);
      loadData();
    } catch (error) {
      console.error("Error deleting item:", error);
      toast.error("Failed to delete item");
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setModalType("create");
    setEditingItem(null);
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (item) => {
    setModalType("edit");
    setEditingItem(item);
    populateForm(item);
    setShowModal(true);
  };

  const populateForm = (item) => {
    switch (activeTab) {
      case "users":
        setUserForm({
          username: item.username || "",
          email: item.email || "",
          password: "",
          role: item.role || "user",
          firstName: item.firstName || "",
          lastName: item.lastName || "",
        });
        break;
      case "dailyItems":
        setDailyItemForm({
          title: item.title || "",
          description: item.description || "",
          assignedTo: item.assignedTo?._id || "",
          createdBy: item.createdBy?._id || "",
          startDate: item.startDate
            ? new Date(item.startDate).toISOString().split("T")[0]
            : "",
          endDate: item.endDate
            ? new Date(item.endDate).toISOString().split("T")[0]
            : "",
          priority: item.priority || "medium",
          status: item.status || "pending",
        });
        break;
      case "deliveryLogs":
        setDeliveryLogForm({
          dailyItem: item.dailyItem?._id || "",
          assignedTo: item.assignedTo?._id || "",
          deliveryDate: item.deliveryDate
            ? new Date(item.deliveryDate).toISOString().split("T")[0]
            : "",
          status: item.status || "pending",
          deliveredBy: item.deliveredBy?._id || "",
          notes: item.notes || "",
        });
        break;
      case "events":
        setEventForm({
          title: item.title || "",
          description: item.description || "",
          start: item.start
            ? new Date(item.start).toISOString().slice(0, 16)
            : "",
          end: item.end ? new Date(item.end).toISOString().slice(0, 16) : "",
          allDay: item.allDay || false,
          color: item.color || "#3174ad",
        });
        break;
      default:
        break;
    }
  };

  const resetForm = () => {
    setUserForm({
      username: "",
      email: "",
      password: "",
      role: "user",
      firstName: "",
      lastName: "",
    });
    setDailyItemForm({
      title: "",
      description: "",
      assignedTo: "",
      createdBy: "",
      startDate: "",
      endDate: "",
      priority: "medium",
      status: "pending",
    });
    setDeliveryLogForm({
      dailyItem: "",
      assignedTo: "",
      deliveryDate: "",
      status: "pending",
      deliveredBy: "",
      notes: "",
    });
    setEventForm({
      title: "",
      description: "",
      start: "",
      end: "",
      allDay: false,
      color: "#3174ad",
    });
  };

  const renderTable = () => {
    switch (activeTab) {
      case "users":
        return (
          <table className="data-table">
            <thead>
              <tr>
                <th>Username</th>
                <th>Email</th>
                <th>Role</th>
                <th>First Name</th>
                <th>Last Name</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user._id}>
                  <td>{user.username}</td>
                  <td>{user.email}</td>
                  <td>{user.role}</td>
                  <td>{user.firstName || "-"}</td>
                  <td>{user.lastName || "-"}</td>
                  <td>
                    <button
                      className="btn btn-edit"
                      onClick={() => openEditModal(user)}
                    >
                      Edit
                    </button>
                    <button
                      className="btn btn-delete"
                      onClick={() => handleDelete(user._id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        );
      case "dailyItems":
        return (
          <table className="data-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Description</th>
                <th>Assigned To</th>
                <th>Created By</th>
                <th>Start Date</th>
                <th>Status</th>
                <th>Priority</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {dailyItems.map((item) => (
                <tr key={item._id}>
                  <td>{item.title}</td>
                  <td>{item.description}</td>
                  <td>
                    {item.assignedTo?.username ||
                      item.assignedTo?.firstName +
                        " " +
                        item.assignedTo?.lastName}
                  </td>
                  <td>
                    {item.createdBy?.username || item.createdBy?.businessName}
                  </td>
                  <td>{new Date(item.startDate).toLocaleDateString()}</td>
                  <td>{item.status}</td>
                  <td>{item.priority}</td>
                  <td>
                    <button
                      className="btn btn-edit"
                      onClick={() => openEditModal(item)}
                    >
                      Edit
                    </button>
                    <button
                      className="btn btn-delete"
                      onClick={() => handleDelete(item._id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        );
      case "deliveryLogs":
        return (
          <table className="data-table">
            <thead>
              <tr>
                <th>Daily Item</th>
                <th>Assigned To</th>
                <th>Delivery Date</th>
                <th>Status</th>
                <th>Delivered By</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {deliveryLogs.map((log) => (
                <tr key={log._id}>
                  <td>{log.dailyItem?.title || "N/A"}</td>
                  <td>
                    {log.assignedTo?.username ||
                      log.assignedTo?.firstName +
                        " " +
                        log.assignedTo?.lastName}
                  </td>
                  <td>{new Date(log.deliveryDate).toLocaleDateString()}</td>
                  <td>{log.status}</td>
                  <td>
                    {log.deliveredBy?.username ||
                      log.deliveredBy?.firstName +
                        " " +
                        log.deliveredBy?.lastName ||
                      "N/A"}
                  </td>
                  <td>
                    <button
                      className="btn btn-edit"
                      onClick={() => openEditModal(log)}
                    >
                      Edit
                    </button>
                    <button
                      className="btn btn-delete"
                      onClick={() => handleDelete(log._id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        );
      case "events":
        return (
          <table className="data-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Description</th>
                <th>Start</th>
                <th>End</th>
                <th>All Day</th>
                <th>Color</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {events.map((event) => (
                <tr key={event._id}>
                  <td>{event.title}</td>
                  <td>{event.description}</td>
                  <td>{new Date(event.start).toLocaleString()}</td>
                  <td>{new Date(event.end).toLocaleString()}</td>
                  <td>{event.allDay ? "Yes" : "No"}</td>
                  <td>
                    <div
                      style={{
                        width: "20px",
                        height: "20px",
                        backgroundColor: event.color,
                        borderRadius: "50%",
                        display: "inline-block",
                      }}
                    ></div>
                  </td>
                  <td>
                    <button
                      className="btn btn-edit"
                      onClick={() => openEditModal(event)}
                    >
                      Edit
                    </button>
                    <button
                      className="btn btn-delete"
                      onClick={() => handleDelete(event._id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        );
      default:
        return null;
    }
  };

  const renderModal = () => {
    if (!showModal) return null;

    const getCurrentForm = () => {
      switch (activeTab) {
        case "users":
          return userForm;
        case "dailyItems":
          return dailyItemForm;
        case "deliveryLogs":
          return deliveryLogForm;
        case "events":
          return eventForm;
        default:
          return {};
      }
    };

    const handleFormChange = (e) => {
      const { name, value } = e.target;
      switch (activeTab) {
        case "users":
          setUserForm((prev) => ({ ...prev, [name]: value }));
          break;
        case "dailyItems":
          setDailyItemForm((prev) => ({ ...prev, [name]: value }));
          break;
        case "deliveryLogs":
          setDeliveryLogForm((prev) => ({ ...prev, [name]: value }));
          break;
        case "events":
          setEventForm((prev) => ({ ...prev, [name]: value }));
          break;
        default:
          break;
      }
    };

    const renderFormFields = () => {
      switch (activeTab) {
        case "users":
          return (
            <>
              <input
                type="text"
                name="username"
                placeholder="Username"
                value={userForm.username}
                onChange={handleFormChange}
                required
              />
              <input
                type="email"
                name="email"
                placeholder="Email"
                value={userForm.email}
                onChange={handleFormChange}
                required
              />
              <input
                type="password"
                name="password"
                placeholder="Password"
                value={userForm.password}
                onChange={handleFormChange}
                required={modalType === "create"}
              />
              <select
                name="role"
                value={userForm.role}
                onChange={handleFormChange}
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
                <option value="superadmin">SuperAdmin</option>
              </select>
              <input
                type="text"
                name="firstName"
                placeholder="First Name"
                value={userForm.firstName}
                onChange={handleFormChange}
              />
              <input
                type="text"
                name="lastName"
                placeholder="Last Name"
                value={userForm.lastName}
                onChange={handleFormChange}
              />
            </>
          );
        case "dailyItems":
          return (
            <>
              <input
                type="text"
                name="title"
                placeholder="Title"
                value={dailyItemForm.title}
                onChange={handleFormChange}
                required
              />
              <textarea
                name="description"
                placeholder="Description"
                value={dailyItemForm.description}
                onChange={handleFormChange}
              />
              <input
                type="text"
                name="assignedTo"
                placeholder="Assigned To (User ID)"
                value={dailyItemForm.assignedTo}
                onChange={handleFormChange}
                required
              />
              <input
                type="text"
                name="createdBy"
                placeholder="Created By (User ID)"
                value={dailyItemForm.createdBy}
                onChange={handleFormChange}
                required
              />
              <input
                type="date"
                name="startDate"
                value={dailyItemForm.startDate}
                onChange={handleFormChange}
                required
              />
              <input
                type="date"
                name="endDate"
                value={dailyItemForm.endDate}
                onChange={handleFormChange}
              />
              <select
                name="priority"
                value={dailyItemForm.priority}
                onChange={handleFormChange}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
              <select
                name="status"
                value={dailyItemForm.status}
                onChange={handleFormChange}
              >
                <option value="pending">Pending</option>
                <option value="in-progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </>
          );
        case "deliveryLogs":
          return (
            <>
              <input
                type="text"
                name="dailyItem"
                placeholder="Daily Item ID"
                value={deliveryLogForm.dailyItem}
                onChange={handleFormChange}
                required
              />
              <input
                type="text"
                name="assignedTo"
                placeholder="Assigned To (User ID)"
                value={deliveryLogForm.assignedTo}
                onChange={handleFormChange}
                required
              />
              <input
                type="date"
                name="deliveryDate"
                value={deliveryLogForm.deliveryDate}
                onChange={handleFormChange}
                required
              />
              <select
                name="status"
                value={deliveryLogForm.status}
                onChange={handleFormChange}
              >
                <option value="pending">Pending</option>
                <option value="delivered">Delivered</option>
                <option value="missed">Missed</option>
                <option value="cancelled">Cancelled</option>
              </select>
              <input
                type="text"
                name="deliveredBy"
                placeholder="Delivered By (User ID)"
                value={deliveryLogForm.deliveredBy}
                onChange={handleFormChange}
              />
              <textarea
                name="notes"
                placeholder="Notes"
                value={deliveryLogForm.notes}
                onChange={handleFormChange}
              />
            </>
          );
        case "events":
          return (
            <>
              <input
                type="text"
                name="title"
                placeholder="Event Title"
                value={eventForm.title}
                onChange={handleFormChange}
                required
              />
              <textarea
                name="description"
                placeholder="Description"
                value={eventForm.description}
                onChange={handleFormChange}
              />
              <input
                type="datetime-local"
                name="start"
                value={eventForm.start}
                onChange={handleFormChange}
                required
              />
              <input
                type="datetime-local"
                name="end"
                value={eventForm.end}
                onChange={handleFormChange}
                required
              />
              <label>
                <input
                  type="checkbox"
                  name="allDay"
                  checked={eventForm.allDay}
                  onChange={(e) =>
                    setEventForm((prev) => ({
                      ...prev,
                      allDay: e.target.checked,
                    }))
                  }
                />
                All Day Event
              </label>
              <input
                type="color"
                name="color"
                value={eventForm.color}
                onChange={handleFormChange}
              />
            </>
          );
        default:
          return null;
      }
    };

    return (
      <div className="modal-overlay">
        <div className="modal-content">
          <h3>
            {modalType === "create" ? "Create" : "Edit"}{" "}
            {activeTab.slice(0, -1)}
          </h3>
          <form onSubmit={modalType === "create" ? handleCreate : handleUpdate}>
            {renderFormFields()}
            <div className="modal-buttons">
              <button
                type="button"
                className="btn btn-cancel"
                onClick={() => setShowModal(false)}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading
                  ? "Saving..."
                  : modalType === "create"
                  ? "Create"
                  : "Update"}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  return (
    <div className="superadmin-dashboard">
      <div className="dashboard-header">
        <h1>SuperAdmin Dashboard</h1>
        <p>Welcome back, {user.username}!</p>
      </div>

      <div className="tabs">
        <button
          className={`tab ${activeTab === "users" ? "active" : ""}`}
          onClick={() => setActiveTab("users")}
        >
          Users
        </button>
        <button
          className={`tab ${activeTab === "dailyItems" ? "active" : ""}`}
          onClick={() => setActiveTab("dailyItems")}
        >
          Daily Items
        </button>
        <button
          className={`tab ${activeTab === "deliveryLogs" ? "active" : ""}`}
          onClick={() => setActiveTab("deliveryLogs")}
        >
          Delivery Logs
        </button>
        <button
          className={`tab ${activeTab === "events" ? "active" : ""}`}
          onClick={() => setActiveTab("events")}
        >
          Events
        </button>
      </div>

      <div className="content">
        <div className="content-header">
          <h2>{activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}</h2>
          <button className="btn btn-primary" onClick={openCreateModal}>
            Create New {activeTab.slice(0, -1)}
          </button>
        </div>

        {loading ? <div className="loading">Loading...</div> : renderTable()}
      </div>

      {renderModal()}
    </div>
  );
};

export default SuperAdminDashboard;
